import type { ReactNode } from 'react'
import React from 'react'
import { useNavigate } from '@tanstack/react-router'
import { useAuth0 } from '@auth0/auth0-react'
import { setTokenGetter } from '@/lib/api'
import { useUserStore } from '@/stores/userStore'
import { useNotificationStore } from '@/stores/notificationStore'
import { createWebSocket, type ManagedWebSocket } from '@/lib/websocket'
import { Sidebar } from './Sidebar'
import { TopBar } from './TopBar'
import { CheckCircle2, X } from 'lucide-react'

const AGENT_LABELS: Record<string, string> = {
  nurturer: 'Nurturer Agent',
  deal_orchestrator: 'DealOrchestrator Agent',
  scheduler: 'Scheduler Agent',
  compliance: 'Compliance Agent',
  lead_qualifier: 'LeadQualifier Agent',
  researcher: 'Research Agent',
  opportunity_watch: 'OpportunityWatch Agent',
  proposal: 'Proposal Agent',
}

interface ApprovalToast {
  approvalId: string
  agentLabel: string
  propertyLabel: string
  preview: string
}

interface AppLayoutProps {
  children: ReactNode
}

export function AppLayout({ children }: AppLayoutProps) {
  const navigate = useNavigate()
  const { isAuthenticated: auth0IsAuthenticated, isLoading: auth0IsLoading, getAccessTokenSilently, loginWithRedirect } = useAuth0()
  const [isAuthenticated, setIsAuthenticated] = React.useState(false)
  const [isLoading, setIsLoading] = React.useState(true)
  const [backendError, setBackendError] = React.useState(false)
  const [accessDenied, setAccessDenied] = React.useState(false)
  const [approvalToast, setApprovalToast] = React.useState<ApprovalToast | null>(null)
  const wsRef = React.useRef<ManagedWebSocket | null>(null)

  const verifyBackend = React.useCallback(async () => {
    setIsLoading(true)
    setBackendError(false)
    setAccessDenied(false)

    if (!auth0IsAuthenticated) {
      setIsAuthenticated(false)
      setIsLoading(false)
      await loginWithRedirect()
      return
    }

    try {
      setTokenGetter(() => getAccessTokenSilently())

      const controller = new AbortController()
      const timer = setTimeout(() => controller.abort(), 10_000)
      const resp = await fetch(`${import.meta.env.VITE_API_BASE_URL}/api/v1/auth/me`, {
        headers: { Authorization: `Bearer ${await getAccessTokenSilently()}` },
        signal: controller.signal,
      })
      clearTimeout(timer)

      if (resp.status === 403) {
        setAccessDenied(true)
        setIsAuthenticated(false)
        return
      }

      if (!resp.ok) throw new Error(`Backend returned ${resp.status}`)

      const meData = await resp.json()
      useUserStore.getState().setUser({
        id: meData.user_id ?? meData.id,
        name: meData.name ?? meData.email ?? meData.sub,
        role: meData.role,
        email: meData.email,
        team_id: meData.team_id,
      })

      setIsAuthenticated(true)
      navigate({ to: '/dashboard' })
    } catch (err) {
      console.error('Auth verification error', err)
      setIsAuthenticated(false)
      setBackendError(true)
    } finally {
      setIsLoading(false)
    }
  }, [auth0IsAuthenticated, getAccessTokenSilently, loginWithRedirect, navigate])

  React.useEffect(() => {
    if (auth0IsLoading) return
    void verifyBackend()
  }, [auth0IsLoading, auth0IsAuthenticated])

  // WebSocket — connect after authentication, disconnect on unmount/logout
  React.useEffect(() => {
    if (!isAuthenticated) return

    let active = true

    const connect = async () => {
      try {
        const accessToken = await getAccessTokenSilently()
        const resp = await fetch(`${import.meta.env.VITE_API_BASE_URL}/auth/ws-token`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${accessToken}` },
        })
        if (!resp.ok) return
        const { token: wsToken } = (await resp.json()) as { token: string }
        if (!active) return

        wsRef.current = createWebSocket(
          `/ws?token=${wsToken}`,
          (event: MessageEvent) => {
            try {
              const msg = JSON.parse(event.data as string) as { type: string; data: Record<string, string | undefined> }
              if (msg.type === 'approval.created') {
                const d = msg.data
                const agentName: string = d.agent_name ?? ''
                const agentLabel: string = d.agent_label ?? AGENT_LABELS[agentName] ?? agentName ?? 'AI Agent'
                const propLabel: string = d.property_label ?? ''
                const rawPreview: string = d.preview ?? d.action_type ?? ''
                const preview: string = propLabel
                  ? `${propLabel}${rawPreview ? ' — ' + rawPreview : ''}`
                  : rawPreview

                setApprovalToast({
                  approvalId: d.approval_id ?? '',
                  agentLabel,
                  propertyLabel: propLabel,
                  preview,
                })

                // Add to notification bell store
                useNotificationStore.getState().addItem({
                  id: `approval-${d.approval_id ?? Date.now()}`,
                  title: `${agentLabel} needs review`,
                  ...(preview ? { body: preview } : {}),
                  type: 'approval',
                  read: false,
                  created_at: new Date().toISOString(),
                })
              }
            } catch {
              // malformed message — ignore
            }
          },
          { maxRetries: 8, retryDelayMs: 3000 },
        )
      } catch (err) {
        console.warn('[WS] Could not connect:', err)
      }
    }

    void connect()

    return () => {
      active = false
      wsRef.current?.close()
      wsRef.current = null
    }
  }, [isAuthenticated, getAccessTokenSilently])

  // Auto-dismiss toast after 7 seconds
  React.useEffect(() => {
    if (!approvalToast) return
    const t = setTimeout(() => setApprovalToast(null), 7000)
    return () => clearTimeout(t)
  }, [approvalToast])

  React.useEffect(() => {
    if (!isAuthenticated && !isLoading && !accessDenied) {
      void loginWithRedirect()
    }
  }, [isAuthenticated, isLoading, accessDenied])

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-bg-primary">
        <div className="flex flex-col items-center gap-3">
          <div className="w-8 h-8 border-2 border-accent-primary border-t-transparent rounded-full animate-spin" />
          <p className="text-sm text-text-secondary">Connecting to Acufy CRM…</p>
        </div>
      </div>
    )
  }

  if (accessDenied) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-bg-primary">
        <div className="flex flex-col items-center gap-4 max-w-sm text-center p-8">
          <div className="w-12 h-12 rounded-full bg-amber-100 flex items-center justify-center">
            <X size={24} className="text-amber-500" />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-text-primary mb-1">Access not granted</h2>
            <p className="text-sm text-text-secondary">
              Your account has not been added to Acufy CRM yet. Contact your administrator to get access.
            </p>
          </div>
          <button
            onClick={() => void verifyBackend()}
            className="px-5 py-2 rounded-xl text-sm font-semibold text-white transition-all"
            style={{ background: 'linear-gradient(135deg, #1E305C, #2d4a8a)' }}
          >
            Try again
          </button>
        </div>
      </div>
    )
  }

  if (backendError) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-bg-primary">
        <div className="flex flex-col items-center gap-4 max-w-sm text-center p-8">
          <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center">
            <X size={24} className="text-red-500" />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-text-primary mb-1">Backend unavailable</h2>
            <p className="text-sm text-text-secondary">
              The CRM server isn't responding. Make sure the backend is running, then retry.
            </p>
          </div>
          <button
            onClick={() => void verifyBackend()}
            className="px-5 py-2 rounded-xl text-sm font-semibold text-white transition-all"
            style={{ background: 'linear-gradient(135deg, #1E305C, #2d4a8a)' }}
          >
            Retry connection
          </button>
        </div>
      </div>
    )
  }

  if (!isAuthenticated) {
    return <>{children}</>
  }

  return (
    <div className="flex h-screen overflow-hidden bg-bg-primary">
      <Sidebar />

      <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
        <TopBar />
        <main className="flex-1 overflow-y-auto">
          {children}
        </main>
      </div>

      {/* Approval toast — bottom-right, auto-dismisses */}
      {approvalToast && (
        <div
          className="fixed bottom-6 right-6 z-50 w-80 bg-bg-secondary border border-bg-tertiary rounded-xl shadow-modal overflow-hidden animate-in fade-in slide-in-from-bottom-4"
          role="alert"
        >
          <div className="h-1 w-full" style={{ background: 'linear-gradient(to right, #1E305C, #A68B5B)' }} />
          <div className="p-4">
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 rounded-lg bg-accent-primary/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                <CheckCircle2 className="w-4 h-4 text-accent-primary" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-text-primary leading-snug">
                  New approval pending
                </p>
                <p className="text-xs text-text-secondary mt-0.5 font-medium">
                  {approvalToast.agentLabel}
                </p>
                {approvalToast.preview && (
                  <p className="text-xs text-text-muted mt-1 truncate">{approvalToast.preview}</p>
                )}
                <button
                  className="mt-2 text-xs font-semibold text-accent-primary hover:underline"
                  onClick={() => {
                    setApprovalToast(null)
                    void navigate({ to: '/approvals' })
                  }}
                >
                  Review now →
                </button>
              </div>
              <button
                className="flex-shrink-0 text-text-muted hover:text-text-primary transition-colors p-0.5 rounded"
                onClick={() => setApprovalToast(null)}
                aria-label="Dismiss"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
